import { cn } from '../cn';
import Button from '../Input2/Button';
import { Alerts, General } from '../StrokeIcons';
import StepIconSvg from '../StepIcon/StepIconSvg';
import type { CampaignDayCardStep, StepDisplaySegment } from './stepDisplayInfo';
import { getSkipLabel, getStepDisplay } from './stepDisplayInfo';

export type CampaignStepRowPureProps = {
  step: CampaignDayCardStep;
  onEdit?: (stepId: string) => void;
  /**
   * Fired when the user clicks the "Mark done" affordance on an
   * action-required reminder row. Only rendered when the step is in
   * the `'action-required'` state AND this callback is provided.
   */
  onMarkFinished?: (stepId: string) => void;
  className?: string;
};

/**
 * Step types whose primary / body text is OUTGOING CONTENT (the actual
 * words the user will send to the contact). Tinted blue so the eye can
 * tell at a glance: "this row is a message I'm authoring" vs "this row
 * is an automated action".
 */
const SENT_TEXT_TYPES: ReadonlySet<string> = new Set([
  'message',
  'connect',
  'inmail',
  'email',
  'reminder',
]);

export function CampaignStepRowPure({
  step,
  onEdit,
  onMarkFinished,
  className,
}: CampaignStepRowPureProps) {
  const display = getStepDisplay(step);
  const extras = display.extras;
  const siftRows = display.siftRows;
  const hasExtras = extras != null && extras.length > 0;
  const hasBody = display.body != null && display.body.length > 0;
  const hasSiftRows = siftRows != null && siftRows.length > 0;
  const hasError = step.error != null && step.error.length > 0;
  // A skipped step (e.g. follow/like rejected by LinkedIn, already connected).
  // Rendered as a muted sub-line — never the red error styling.
  const skipLabel = getSkipLabel(step);
  const hasSkipLabel = skipLabel != null;
  // Manual reminders waiting on the user. Tinted green so they stand
  // out as actionable: "I need to do this myself."
  const isActionRequired = step.type === 'reminder' && step.state === 'action-required';
  // Whether the primary line holds wrappable body content (message body,
  // connect note, subject-less inmail/email body) vs. a one-line subject or
  // short action label. Decided in getStepDisplay so both row renderers
  // agree; the separate body block below always wraps independently.
  const wrapPrimary = display.wrapPrimary ?? false;
  const isMultiLine = hasExtras || hasBody || hasSiftRows || hasError || hasSkipLabel || wrapPrimary;
  const isSentText = SENT_TEXT_TYPES.has(step.type);
  // No onEdit → read-only row (e.g. the extension sidepanel, which can't
  // edit steps yet). Drop the button semantics, pointer cursor, and hover
  // tints so the row doesn't promise an interaction it can't deliver.
  const interactive = onEdit != null;

  return (
    <div
      data-testid="campaign-step-row"
      data-step-id={step.id}
      data-step-type={step.type}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onEdit(step.id) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEdit(step.id);
              }
            }
          : undefined
      }
      className={cn(
        'group flex w-full gap-3 px-3 py-2 transition-colors',
        isMultiLine ? 'items-start' : 'items-center',
        interactive && 'cursor-pointer',
        // Resting tint precedence: error (red) > action-required reminder
        // (green, "needs you") > template-diff (purple). Sent-text and
        // action rows have no resting bg.
        hasError
          ? 'bg-fair-pink-50 dark:bg-red-950/40'
          : isActionRequired
            ? 'bg-mantis-50 dark:bg-mantis-950/30'
            : step.isDifferentFromTemplate
              ? 'bg-purple-50/40 dark:bg-purple-950/20'
              : undefined,
        // Hover bg only when the row is clickable. It echoes whichever
        // signal owns the row's resting tint, so the user gets visual
        // feedback that matches the row instead of a neutral gray hover.
        interactive &&
          (hasError
            ? 'hover:bg-fair-pink-100 dark:hover:bg-red-950/60'
            : isActionRequired
              ? 'hover:bg-mantis-100 dark:hover:bg-mantis-950/50'
              : step.isDifferentFromTemplate
                ? 'hover:bg-purple-50 dark:hover:bg-purple-950/40'
                : isSentText
                  ? // Faint astronaut tint on hover to match the blue
                    // sent-text rows (message / connect / inmail / email /
                    // reminder). No resting bg — only revealed on hover.
                    'hover:bg-astronaut-50 dark:hover:bg-astronaut-950/40'
                  : // Action rows (visit / follow / like / sift) stay neutral.
                    'hover:bg-lynch-100 dark:hover:bg-slate-800'),
        className,
      )}>
      <StepIconSvg
        iconType={step.type}
        className={cn(
          'size-4 shrink-0',
          // Icon follows the same tint precedence as the row bg.
          hasError
            ? 'text-red-500 dark:text-red-300'
            : isActionRequired
              ? 'text-mantis-600 dark:text-mantis-300'
              : step.isDifferentFromTemplate
                ? 'text-purple-500 dark:text-purple-300'
                : 'text-lynch-500 dark:text-lynch-400',
          isMultiLine && 'mt-1',
        )}
        data-testid="campaign-step-icon"
      />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <span
          className={cn(
            'text-base font-medium',
            // Tint precedence on text: error red > action-required
            // green > sent-text blue > muted action gray.
            hasError
              ? 'text-red-700 dark:text-red-300'
              : isActionRequired
                ? 'text-mantis-700 dark:text-mantis-300'
                : isSentText
                  ? // Sent-text rows go a deep `astronaut-800` blue.
                    'text-astronaut-800 dark:text-astronaut-200'
                  : // Action rows fade to `lynch-400` (light blue-gray) —
                    // ~3.5:1 contrast against sent-text so the eye
                    // immediately clocks "automated action" vs "outgoing
                    // message".
                    'text-lynch-400 dark:text-lynch-500',
            wrapPrimary ? 'whitespace-pre-wrap wrap-break-word' : 'truncate',
          )}
          data-testid="campaign-step-primary">
          {display.primarySegments != null ? (
            <SegmentText segments={display.primarySegments} />
          ) : (
            display.primary
          )}
        </span>
        {hasExtras && extras && (
          <div className="flex flex-col" data-testid="campaign-step-extras">
            {extras.map((extra) => (
              <span
                key={extra.label}
                className="wrap-break-word text-xs text-lynch-700 dark:text-lynch-300"
                data-testid={`campaign-step-extra-${extra.label.toLowerCase()}`}>
                <span className="text-[10px] uppercase tracking-wide text-lynch-700 dark:text-lynch-300">
                  {extra.label}
                </span>{' '}
                {extra.value}
              </span>
            ))}
          </div>
        )}
        {hasBody && (
          <span
            className={cn(
              'whitespace-pre-wrap wrap-break-word text-base',
              hasError
                ? 'text-red-700 dark:text-red-300'
                : isActionRequired
                  ? 'text-mantis-700 dark:text-mantis-300'
                  : isSentText
                    ? 'text-astronaut-800 dark:text-astronaut-200'
                    : 'text-lynch-400 dark:text-lynch-500',
            )}
            data-testid="campaign-step-body">
            {display.bodySegments != null ? (
              <SegmentText segments={display.bodySegments} />
            ) : (
              display.body
            )}
          </span>
        )}
        {hasSiftRows && siftRows && (
          <ul
            className="flex flex-col gap-1"
            data-testid="campaign-step-sift-rows">
            {siftRows.map((row, idx) => (
              <li
                key={idx}
                data-testid="campaign-step-sift-row"
                className="flex flex-wrap items-center gap-1.5 text-sm leading-tight">
                <span
                  data-testid="campaign-step-sift-field"
                  className="font-semibold text-lynch-900 dark:text-lynch-100">
                  {row.field}
                </span>
                <span
                  data-testid="campaign-step-sift-operator"
                  className="text-lynch-500 dark:text-lynch-400">
                  {row.operator}
                </span>
                {row.value !== undefined && (
                  <span
                    data-testid="campaign-step-sift-value"
                    className={cn(
                      'rounded-md px-1.5 py-0.5 font-mono text-xs',
                      row.value.length === 0
                        ? 'bg-lynch-100 text-lynch-500 dark:bg-slate-800 dark:text-lynch-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
                    )}>
                    {row.value.length === 0 ? '…' : row.value}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {hasError && (
          <span
            className="mt-0.5 inline-flex items-start gap-1 wrap-break-word text-xs text-red-700 dark:text-red-300"
            data-testid="campaign-step-error">
            <Alerts.AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
            <span>{step.error}</span>
          </span>
        )}
        {hasSkipLabel && (
          <span
            className="mt-0.5 inline-flex items-start gap-1 wrap-break-word text-xs text-lynch-500 dark:text-lynch-400"
            data-testid="campaign-step-skip">
            <General.XCircle className="mt-px size-3.5 shrink-0" aria-hidden />
            <span>{skipLabel}</span>
          </span>
        )}
      </div>
      {isActionRequired && onMarkFinished != null && (
        <Button
          data-testid="campaign-step-mark-done"
          // Stop the row's onClick from firing (which would open the
          // editor) — the user clicked the explicit done affordance.
          onClick={(e) => {
            e.stopPropagation();
            onMarkFinished(step.id);
          }}
          size="2xs"
          color="green"
          icon={<General.Check />}
          tooltip="Mark this reminder as done"
          className="self-center">
          Mark done
        </Button>
      )}
    </div>
  );
}

/**
 * Renders a list of `StepDisplaySegment`s with variable runs styled
 * distinctly so the user can tell which words came from a variable
 * (and will be re-resolved per contact at send time) vs. which were
 * authored as plain text. Variables get a chip-style background +
 * stronger text colour while remaining inline so the surrounding
 * sentence still flows naturally.
 */
function SegmentText({ segments }: { segments: StepDisplaySegment[] }) {
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === 'variable' ? (
          <span
            key={i}
            data-testid="campaign-step-variable"
            data-variable-id={seg.id}
            // Stronger, saturated colour vs. the surrounding text. Uses
            // `cold-purple` (the project's distinct purple palette) so
            // it doesn't collide with `astronaut` (sent-text blue) or
            // `purple` (template-diff signal). NO horizontal padding —
            // it would create phantom whitespace whenever a variable is
            // adjacent to punctuation (`Michael-Charkin` → "Michael -
            // Charkin"). Just colour + weight is enough to distinguish.
            className="font-semibold text-cold-purple-800 dark:text-cold-purple-200">
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}
